import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';

import UserBadge from '@/features/user-profile/components/UserBadge.vue';
import TextRecommendationModal from './TextRecommendationModal.vue';
import Popover from '@/ui-components/Popover.vue';

import type { TextRecommendationFragment } from '@/features/why-to-watch/graphql/fragments/TextRecommendation.fragment';
import type { TitleDetail } from '@/interfaces/title-details-graphql';

import { useLanguageStore } from '@/helpers/composables/useStores';
import { preventNonSignedInAction } from '@/helpers/prevent-non-signed-in-action-helper';
import { useMutation } from '@/helpers/composables/useApollo';
import {
	LikeTextRecommendationDocument,
	LikeTextRecommendationMutation,
	LikeTextRecommendationMutationVariables,
} from '@/features/why-to-watch/graphql/mutations/LikeTextRecommendation.mutation';
import {
	DeleteTextRecommendationDocument,
	DeleteTextRecommendationMutation,
	DeleteTextRecommendationMutationVariables,
} from '@/features/why-to-watch/graphql/mutations/DeleteTextRecommendation.mutation';
import { toastError, toastSuccess } from '@/helpers/toast/toast-helper';
import { AccountModes } from '@/enums/login';

import { useI18n } from '@/helpers/composables/useI18n';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SnowplowContext } from '@/helpers/tracking/providers';
import { formatDateTime } from '@/helpers/date-helper';
import { disableScroll, enableScroll } from '@/helpers/scroll-helper';
import { faPen, faTrash, faEllipsis, faThumbsUp } from '@fortawesome/free-solid-svg-icons';


export default /*#__PURE__*/_defineComponent({
  __name: 'TextRecommendationCardModal',
  props: {
    textRecommendation: null,
    title: null,
    additionalContexts: null
  },
  emits: ["close"],
  setup(__props: any, { emit: emits }: { emit: ({
	(e: 'close', display: boolean): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	textRecommendation: TextRecommendationFragment;
	title: TitleDetail;
	additionalContexts?: SnowplowContext[];
};

const BaseModalNew = () => import('@/components/modals/BaseModalNew.vue');





const { t, tc } = useI18n();

const { language, country } = useLanguageStore();

const displayTextRecommendationModal = ref<boolean>(false);
const showOriginalText = ref<boolean>(false);

const liked = computed(() => props.textRecommendation.likedByUser);
const likeCount = computed(() => props.textRecommendation.likeCount);

const profile = computed(() => props.textRecommendation.profile);

const headline = computed(() => props.textRecommendation.headline);
const originalHeadline = computed(() => props.textRecommendation.originalHeadline);
const body = computed(() => props.textRecommendation.body);
const originalBody = computed(() => props.textRecommendation.originalBody);

const displayedHeadline = computed(() => {
	if (showOriginalText.value && originalHeadline.value) return originalHeadline.value;
	return headline.value;
});

const displayedBody = computed(() => {
	if (showOriginalText.value && originalBody.value) return originalBody.value;
	return body.value;
});

const date = computed(() => {
	const date = new Date(props.textRecommendation.watchedAt);

	return formatDateTime(date, language.value, country.value, {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
	});
});

const watchedInCinema = computed(() => {
	return props.textRecommendation.watchedOn?.packageId === 0;
});

const watchedOnText = computed(() => {
	if (watchedInCinema.value) return t('WEBAPP_WATCHED_CINEMA', { date: date.value });

	return t('WEBAPP_WATCHED_PROVIDER', { provider: props.textRecommendation.watchedOn?.clearName, date: date.value });
});

const backgroundImageUrl = computed(() => JW_CONFIG.IMAGESCALER_URL + props.title.content.fullPosterUrl);

const tagsString = computed(() => {
	const tags = props.textRecommendation.tags;
	if (!tags || tags.length === 0) return '';

	return tags.map(tag => `#${tag.translatedName}`).join(' ');
});

const watchedOn = computed(() => props.textRecommendation.watchedOn);

const formData = computed(() => {
	return {
		id: props.textRecommendation.id,
		headline: headline.value,
		body: body.value,
		tags: props.textRecommendation.tags,
		watchedOn: watchedOn.value,
		watchedAt: props.textRecommendation.watchedAt,
		profile: props.textRecommendation.profile,
		ownedByUser: props.textRecommendation.ownedByUser,
		likeCount: props.textRecommendation.likeCount,
	};
});

const showPopover = computed(() => {
	return props.textRecommendation.ownedByUser;
});

const isTranslated = computed(() => {
	return !!(props.textRecommendation.originalHeadline || props.textRecommendation.originalBody);
});

function handleShowTranslation() {
	showOriginalText.value = !showOriginalText.value;
}

const likeRecommendation = useMutation<LikeTextRecommendationMutation, LikeTextRecommendationMutationVariables>(
	LikeTextRecommendationDocument,
	{
		refetchQueries: ['GetTextRecommendations'],
	}
);

function handleLikeClick() {
	const likeValue = !liked.value;

	likeRecommendation.mutate({
		input: {
			uuid: props.textRecommendation.id,
			like: likeValue,
		},
		country: country.value,
		language: language.value,
	});

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'helpful_clicked',
			value: liked.value ? 1 : 0,
		},
		props.additionalContexts
	);
}

const deleteRecommendation = useMutation<DeleteTextRecommendationMutation, DeleteTextRecommendationMutationVariables>(
	DeleteTextRecommendationDocument,
	{
		refetchQueries: ['GetTextRecommendations'],
	}
);

function deleteTextRecommendation() {
	deleteRecommendation.mutate({
		input: {
			id: props.textRecommendation.id,
		},
	});
}

deleteRecommendation.onDone(() => {
	toastSuccess({
		message: tc('WEBAPP_RECOMMENDATION_DELETED_SUCCESS'),
		duration: 3000,
	});
});

deleteRecommendation.onError(() => {
	toastError({
		message: tc('WEBAPP_RECOMMENDATION_DELETED_ERROR'),
		duration: 3000,
	});
});

function toggleTextRecommendationModal(value: boolean) {
	if (preventNonSignedInAction(AccountModes.REGISTRATION_OPTIONS)) return;

	displayTextRecommendationModal.value = value;

	displayTextRecommendationModal.value ? disableScroll() : enableScroll();
}

return { __sfc: true,BaseModalNew, props, emits, t, tc, language, country, displayTextRecommendationModal, showOriginalText, liked, likeCount, profile, headline, originalHeadline, body, originalBody, displayedHeadline, displayedBody, date, watchedInCinema, watchedOnText, backgroundImageUrl, tagsString, watchedOn, formData, showPopover, isTranslated, handleShowTranslation, likeRecommendation, handleLikeClick, deleteRecommendation, deleteTextRecommendation, toggleTextRecommendationModal, UserBadge, TextRecommendationModal, Popover, faPen, faTrash, faEllipsis, faThumbsUp }
}

})